import { action, makeObservable, observable, runInAction } from 'mobx';
import { ICompositeProductApi } from '@canvas-logic/clp-sdk';

import {
  attributeValueCase,
  createAttributeValue,
  getAttributeValue,
  hasAttributeValue,
  ICfgAttributeValue,
  RAL,
  ralCodeToHex,
  ralToHex,
} from '@canvas-logic/clp-domain';

import React from 'react';
import { RalAttributeCfgUIElement } from '@/UiBuilder/elements/RalAttributeCfgUIElement/RalAttributeCfgUIElement';
import { IUICfgAttributeElementSettings, IUICfgAttributeRal } from '@canvas-logic/clp-api-types';
import { AttributeCfgUIElementStateBase } from './AttributeCfgUIElementStateBase';

let id = 0;

export interface Color {
  ral: string;
  hex: string;
}

export class RalAttributeCfgUIElementState extends AttributeCfgUIElementStateBase<IUICfgAttributeRal> {
  public id = 'ral-view-state-' + ++id;

  color: {
    custom: boolean;
    value: Color;
  } | null = null;
  availableColors: Color[] = [];
  customColors: Color[] = [];
  loading = false;
  hasCustom = false;

  constructor(public readonly api: ICompositeProductApi, element: IUICfgAttributeRal) {
    super(api, element);
    // TODO: validate element
    // this.visible = this.api.isAvailable(this.componentKey);
    this.updateCurrent();
    if (!this.color) {
      throw new Error('Empty color');
    }
    this.availableColors = [this.color.value];

    makeObservable(this, {
      loading: observable,
      customColors: observable,
      availableColors: observable,
      color: observable,
      hasCustom: observable,
      update: action.bound,
      loadCustomColors: action.bound,
      setColor: action.bound,
      setCustomColor: action.bound,
    });

    this.update().then(() => this.loadCustomColors());
  }

  async update() {
    super.update();
    if (this.visible) {
      this.loading = true;
      const values = (await this.api.loadCompatibleAttributeValues(this.componentKey, this.attributeKey)) ?? [];
      runInAction(() => {
        this.loading = false;
        this.availableColors = [];
        values.forEach((value) => {
          const valueCase = attributeValueCase(value);
          switch (valueCase.type) {
            case 'value':
              const ral = valueCase.value.toString();
              this.availableColors.push({
                ral,
                hex: ralToHex(ral),
              });
              break;
            case 'emptyCustom':
              this.hasCustom = true;
              break;
            case 'customValue':
              throw new Error('Invalid attribute value');
          }
        });
        this.updateCurrent();
      });
    }
  }

  async setColor(colorRal: string) {
    const attributeValue = createAttributeValue(colorRal);
    await this.api.setAttributeValue(this.componentKey, this.attributeKey, attributeValue);
    await this.update();
    this.handleUpdate();
  }

  async setCustomColor(colorRal: string) {
    const attributeValue: ICfgAttributeValue = { custom: true, value: colorRal };
    await this.api.setAttributeValue(this.componentKey, this.attributeKey, attributeValue);
    await this.update();
    this.handleUpdate();
  }

  private handleUpdate() {
    this.notify({
      event: 'updateDependentAttributes',
      attributes: [],
      target: this,
    });
  }

  private updateCurrent() {
    const colorAttributeValue = this.api.getAttributeValue(this.componentKey, this.attributeKey);
    const code = getAttributeValue(colorAttributeValue).toString();
    const color: Color = {
      ral: code,
      hex: ralToHex(code),
    };
    this.color = {
      custom: colorAttributeValue.custom ?? false,
      value: color,
    };
  }

  async loadCustomColors() {
    this.customColors = [];
    const values = await this.api.loadCompatibleAttributeValues(this.componentKey, this.attributeKey);
    const usedRALs = new Set<string>();

    runInAction(() => {
      for (const attributeValue of values) {
        if (hasAttributeValue(attributeValue)) {
          usedRALs.add(attributeValue.value as string);
        }
      }
      for (const code of RAL.codes) {
        const ral = `RAL ${code}`;
        if (!usedRALs.has(ral)) {
          this.customColors.push({
            ral: ral,
            hex: ralCodeToHex(code),
          });
        }
      }
    });
  }

  render(): React.ReactElement {
    return <RalAttributeCfgUIElement key={this.id} state={this} textData={this.textData} />;
  }
}
